/* eslint-disable import/no-cycle */
/* eslint-disable max-lines */
import { CellProps } from "react-table";
import i18next from "i18next";
import React from "react";
import { Lozenge } from "components/Novus/Lozenge";
import { getStatusStyle } from "components/commonUtilities";
import { ExportFileType } from "../../../../../actions/regulation/Listview/types";
import { CurbLinkRecord } from "../../../../../interfaces/CurbLink";
import { ProposedChangesFilter } from "./filters";
import { formatDate } from "../../../../../Utilities/dateTimeUtilities";
import { truncateText } from "../../../../../Utilities/generalUtilities";
import { PermitParkingValue } from "../../../../../actions/Filters/types";

export interface ActionButton {
    disabled: boolean;
    variant: string;
    onClick?: () => void;
    name: string;
    icon?: React.ReactNode;
}

interface StreetNames {
    // eslint-disable-next-line camelcase
    curb_link: CurbLinkRecord | null;
    // eslint-disable-next-line camelcase
    custom_link: CurbLinkRecord | null;
}

export const columns = [
    {
        accessor: "regulationId",
        Filter: ProposedChangesFilter,
        filterParam: "regulationId__in",
        Header: "Policy ID",
        id: "regulationId"
    },
    {
        accessor: "sub_activity",
        Cell: (props: CellProps<{}>): string =>
            props.value.has_multiple_regulations
                ? i18next.t("Multiple")
                : i18next.t(props.value.name),
        Filter: ProposedChangesFilter,
        filterParam: "sub_activity__activity__id__in",
        Header: "Policy",
        id: "sub_activity__activity__name"
    },
    {
        accessor: "action",
        Cell: (props: CellProps<{}>): string => i18next.t(props.value),
        Filter: ProposedChangesFilter,
        filterParam: "action__in",
        Header: "Action",
        id: "action"
    },
    {
        accessor: "is_published",
        Header: "Status",
        id: "status",
        Cell: (props: CellProps<{}>): JSX.Element => {
            const published = props.value === true;

            return (
                <Lozenge
                    text={published ? "Published" : "Unpublished"}
                    type={getStatusStyle(published ? "Complete" : "To Do")}
                />
            );
        }
    },
    {
        accessor: "proposedregulationlinks_set",
        Cell: (props: CellProps<{}>): string => {
            if (props.value.length) {
                const streetNames = new Set();

                props.value.forEach((names: StreetNames) => {
                    if (names.curb_link) {
                        streetNames.add(names.curb_link.bl_street_name);
                    } else if (names.custom_link) {
                        streetNames.add(names.custom_link.bl_street_name);
                    }
                });
                if (streetNames.size > 0) {
                    if (streetNames.size > 1) return "Multiple";

                    return i18next.t(streetNames.values().next().value as string);
                }

                return "";
            }

            return "";
        },
        Filter: ProposedChangesFilter,
        filterParam: "proposedregulationlinks_set__curb_link__bl_street_name__in",
        Header: "Street",
        id: "proposedregulationlinks__curb_link__bl_street_name"
    },
    {
        accessor: "bl_side",
        Cell: (props: CellProps<{}>): string => i18next.t(props.value),
        Filter: ProposedChangesFilter,
        filterParam: "bl_side__in",
        Header: "Side",
        id: "bl_side"
    },
    {
        accessor: "bl_from",
        Filter: ProposedChangesFilter,
        filterParam: "bl_from__in",
        Header: "From",
        id: "bl_from"
    },
    {
        accessor: "bl_to",
        Filter: ProposedChangesFilter,
        filterParam: "bl_to__in",
        Header: "To",
        id: "bl_to"
    },
    {
        accessor: "created",
        Cell: (props: CellProps<{}>): string => {
            if (!props.value) {
                return "";
            }

            if (typeof props.value === "string" && props.value.length >= 10) {
                return props.value.slice(0, 10);
            }

            const parsedDate = new Date(props.value);

            if (!Number.isNaN(parsedDate.getTime())) {
                return parsedDate.toISOString().slice(0, 10);
            }

            return formatDate(props.value);
        },
        Filter: ProposedChangesFilter,
        filterParam: "created__date__in",
        Header: "Created On",
        id: "created"
    },
    {
        accessor: "proposedregulationlinks_set",
        Cell: (props: CellProps<{}>): string => {
            if (props.value.length) {
                const baysAngle = new Set();

                props.value.forEach((names: StreetNames) => {
                    if (names.curb_link) {
                        baysAngle.add(names.curb_link.bays_angle);
                    } else if (names.custom_link) {
                        baysAngle.add(names.custom_link.bays_angle);
                    }
                });
                if (baysAngle.size > 0) {
                    if (baysAngle.size > 1) return i18next.t("Multiple");

                    return i18next.t(baysAngle.values().next().value as string);
                }

                return "";
            }

            return "";
        },
        Filter: ProposedChangesFilter,
        filterParam: "proposedregulationlinks_set__curb_link__bays_angle__in",
        Header: "Bays Angle",
        id: "proposedregulationlinks__curb_link__bays_angle"
    },
    {
        accessor: "proposedregulationlinks_set",
        Cell: (props: CellProps<{}>): string => {
            if (props.value.length) {
                const baysCount = new Set();

                props.value.forEach((names: StreetNames) => {
                    if (names.curb_link) {
                        baysCount.add(names.curb_link.bays_count);
                    } else if (names.custom_link && names.custom_link.bays_count) {
                        baysCount.add(names.custom_link.bays_count);
                    }
                });
                if (baysCount.size > 0) {
                    if (baysCount.size > 1) return "Multiple";

                    return baysCount.values().next().value as string;
                }

                return "";
            }

            return "";
        },
        Filter: ProposedChangesFilter,
        filterParam: "proposedregulationlinks_set__curb_link__bays_count__in",
        Header: "Bays Count",
        id: "proposedregulationlinks__curb_link__bays_count"
    },
    {
        accessor: "proposedregulationlinks_set",
        Cell: (props: CellProps<{}>): string => {
            if (props.value.length) {
                const segmentIds = new Set();

                props.value.forEach((names: StreetNames) => {
                    if (names.curb_link) {
                        segmentIds.add(names.curb_link.segment_id);
                    } else if (names.custom_link) {
                        segmentIds.add(names.custom_link.segment_id);
                    }
                });
                if (segmentIds.size > 0) {
                    if (segmentIds.size > 1) return i18next.t("Multiple");

                    return segmentIds.values().next().value as string;
                }

                return "";
            }

            return "";
        },
        Filter: ProposedChangesFilter,
        filterParam: "proposedregulationlinks_set__curb_link__segment_id__in",
        Header: "Segment Id",
        id: "proposedregulationlinks__curb_link__segment_id"
    },

    {
        accessor: "permits",
        Cell: (props: CellProps<{}>): string =>
            props.value
                ?.filter(
                    (permit: PermitParkingValue) =>
                        permit.permit_type.toLowerCase() === "allowed" ||
                        permit.permit_type.toLowerCase() === "both"
                )
                ?.map((permit: PermitParkingValue) => i18next.t(permit.name))
                ?.join(", ") || "",

        Filter: ProposedChangesFilter,
        filterParam: "permits_allowed__name__in",
        Header: "Permits Allowed",
        id: "permitsAllowed"
    },
    {
        accessor: "permits",
        Cell: (props: CellProps<{}>): string =>
            props.value
                ?.filter(
                    (permit: PermitParkingValue) =>
                        permit.permit_type.toLowerCase() === "exempted" ||
                        permit.permit_type.toLowerCase() === "both"
                )
                ?.map((permit: PermitParkingValue) => i18next.t(permit.name))
                ?.join(", ") || "",
        Filter: ProposedChangesFilter,
        filterParam: "permits_exempted__name__in",
        Header: "Permits Exempted",
        id: "permitsExempted"
    },
    {
        accessor: "sub_activity",
        Cell: (props: CellProps<{}>): string =>
            props.value.has_multiple_regulations
                ? i18next.t("Multiple")
                : i18next.t(props.value.activity.name),
        Filter: ProposedChangesFilter,
        filterParam: "sub_activity__activity__id__in",
        Header: "Activity",
        id: "activity_name"
    },
    {
        accessor: "time_limit",
        Cell: (props: CellProps<{}>): string =>
            props.value !== null ? i18next.t(props.value) : "",
        Filter: ProposedChangesFilter,
        filterParam: "time_limit__in",
        Header: "Max Stay",
        id: "time_limit"
    },
    {
        accessor: "payment_status",
        Cell: (props: CellProps<{}>): string =>
            props.value !== null ? i18next.t(props.value) : "",
        Filter: ProposedChangesFilter,
        filterParam: "payment_status__in",
        Header: "Payment Status",
        id: "payment_status"
    },
    {
        accessor: "effective_dates_period",
        Cell: (props: CellProps<{}>): string =>
            props.value !== null ? i18next.t(props.value) : "",
        Filter: ProposedChangesFilter,
        filterParam: "effective_dates_period__in",
        Header: "Period",
        id: "period"
    },
    {
        accessor: "priority",
        Cell: (props: CellProps<{}>): string =>
            props.value !== null ? i18next.t(props.value) : "",
        Filter: ProposedChangesFilter,
        filterParam: "priority__in",
        Header: "Priority",
        id: "priority"
    },
    {
        accessor: "payment",
        Cell: (props: CellProps<{}>): string =>
            props.value.fees !== null ? i18next.t(props.value.fees[0]) : "",
        Filter: ProposedChangesFilter,
        filterParam: "payment__fees__in",
        Header: "Fees",
        id: "payment__fees"
    },
    {
        accessor: "payment",
        Cell: (props: CellProps<{}>): string =>
            props.value.duration !== null ? i18next.t(props.value.duration[0]) : "",
        Filter: ProposedChangesFilter,
        filterParam: "payment__duration__in",
        Header: "Duration",
        id: "payment__duration"
    },
    {
        accessor: "bl_regulation_desc",
        Cell: (props: CellProps<{}>): string =>
            props.value !== null ? i18next.t(props.value) : "",
        Filter: ProposedChangesFilter,
        filterParam: "bl_regulation_desc__in",
        Header: "Regulation Description",
        id: "bl_regulation_desc"
    },
    {
        accessor: "regulationId",
        Cell: (props: CellProps<{}>): string =>
            props.value !== null ? i18next.t(props.value) : "",
        Filter: ProposedChangesFilter,
        filterParam: "regulationId__in",
        Header: "RegulationId",
        id: "RegulationId"
    },
    {
        accessor: "user.username",
        Filter: ProposedChangesFilter,
        filterParam: "user_id__in",
        Header: "User",
        id: "user__username"
    },
    {
        accessor: "effective_dates_period",
        Filter: ProposedChangesFilter,
        filterParam: "effective_dates_period__in",
        Header: "Period",
        id: "effective_dates_period"
    },

    {
        accessor: "published_on",
        Cell: (props: CellProps<{}>): string =>
            props.value !== null ? formatDate(props.value) : "",
        Filter: ProposedChangesFilter,
        filterParam: "published_on__date__in",
        Header: "Published On",
        id: "published_on"
    },
    {
        accessor: "proposedregulationlinks_set",
        Cell: (props: CellProps<{}>): string => {
            if (props.value[0]?.batch) {
                return props.value[0].batch.id;
            }

            return "";
        },
        Filter: ProposedChangesFilter,
        filterParam: "proposedregulationlinks_set__batch__id__in",
        Header: "Batch Id",
        id: "proposedregulationlinks_set__batch__id"
    },
    {
        accessor: "description",
        Cell: (props: CellProps<{}>): React.ReactElement => {
            const fullText = props.value || "";
            const truncated = truncateText(fullText, 50);

            return React.createElement("div", { title: fullText }, truncated);
        },
        Header: "Description",
        id: "description",
        disableSortBy: true
    }
];

export const defaultColumnNames = [
    "Policy ID",
    "Policy",
    "Action",
    "Status",
    "Street",
    "Side",
    "From",
    "To",
    "Created On"
];

export const additionalColumnNames = [
    "Activity",
    "Batch Id",
    "User",
    "Duration",
    "Published On",
    "Bays Angle",
    "Bays Count",
    "Max Stay",
    "Payment Status",
    "Period",
    "Priority",
    "Duration",
    "Period",
    "Fees",
    "Regulation Description",
    "RegulationId",
    "Segment Id",
    "Permits Allowed",
    "Permits Exempted",
    "Description"
];

export const fields =
    "bl_side,bl_from,bl_to,permits,regulationId,user__username,created,action,time_limit,payment_status,priority,payment,bl_regulation_desc,sub_activity__name,sub_activity__dash_style,sub_activity__color,sub_activity__activity__name,sub_activity__id,id,proposedregulationlinks_set__curb_link__bays_angle,proposedregulationlinks_set__curb_link__segment_id,proposedregulationlinks_set__curb_link__geometry,proposedregulationlinks_set__custom_link__segment_id,proposedregulationlinks_set__custom_link__geometry,proposedregulationlinks_set__curb_link__bays_count,proposedregulationlinks_set__curb_link__bl_street_name,proposedregulationlinks_set__custom_link__bl_street_name,proposedregulationlinks_set__custom_link__id,proposedregulationlinks_set__curb_link__id,actual_regulation_id,is_published,published_on,proposedregulationlinks_set__custom_link__parent_link,proposedregulationlinks_set__batch__id,proposedregulationlinks_set__batch__has_multiple_regulations,proposedregulationlinks_set__id,effective_dates_period,description";

export const batchFields =
    "regulation__bl_side,regulation__bl_from,regulation__bl_to,regulation__regulationId,regulation__user__username,created,regulation__action,regulation__sub_activity__name,regulation__sub_activity__activity__name,regulation__id,curb_link__bl_street_name,custom_link__bl_street_name,custom_link__id,curb_link__id,regulation__actual_regulation_id,regulation__is_published,regulation__published_on,custom_link__parent_link,batch,id,regulation__effective_dates_period";

export const excelFields = {
    Action: "Action",
    Activity: "Activity",
    "Bays Angle": "baysAngle",
    "Bays Count": "baysCount",
    "Created On": "Date Created",
    "Date Exported": "Date Exported",
    Description: "description",
    Duration: "rate.durations",
    Fees: "rate.fees",
    From: "BLfrom",
    "Max Stay": "maxStay",
    "Payment Status": "payment",
    Period: "effective_dates_period",
    Priority: "priority",
    "Published On": "Date Published",
    Reason: "Reason",
    "Regulation Description": "Regulationdescription",
    RegulationId: "regulationID",
    "Segment Id": "SegmentId",
    Side: "BLside",
    "Street Name": "BLstreetname",
    To: "BLto",
    User: "user"
};

export const pdfFields = {
    Action: "action",
    Activity: "sub_activity",
    "Bays Angle": "bays_angle",
    "Bays Count": "bays_count",
    "Created On": "created",
    "Date Exported": "date_exported",
    Description: "description",
    Duration: "payment",
    Fees: "payment",
    From: "bl_from",
    "Max Stay": "time_limit",
    "Payment Status": "payment_status",
    Period: "effective_dates_period",
    Priority: "priority",
    "Published On": "published_on",
    Reason: "sub_activity",
    "Regulation Description": "regul_desc",
    RegulationID: "regulationId",
    SegmentID: "segment_id",
    Side: "bl_side",
    "Street Name": "bl_streetname",
    To: "bl_to"
};

interface Data {
    id?: string;
    // eslint-disable-next-line camelcase
    is_published?: boolean;
    proposedregulationlinks_set?: [
        {
            custom_link: {
                parent_link: number;
            };
            curb_link: {
                id: number;
            };
        }
    ];
}
export interface RegulationData {
    original: Data;
}

export const exportFileType: { [key in ExportFileType]: ExportFileType } = {
    excel: "excel",
    json: "json",
    pdf: "pdf"
};

export const exportFileExtention: { [key in ExportFileType]: string } = {
    excel: ".xlsx",
    json: ".geojson",
    pdf: ".pdf"
};

export const exportFileExtentionForAnalyzer: { [key in ExportFileType]: string } = {
    excel: ".xlsx",
    json: ".json",
    pdf: ".pdf"
};
